import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, LogOut, RefreshCw, Settings, Trash2, Volume2 } from 'lucide-react';
import * as api from './api';

const displayDate = value => value ? new Date(value).toLocaleString() : 'Not available';

const statusLabel = status => ({
  ready: 'Connected',
  refreshing: 'Refreshing',
  reauth_required: 'Reauthorization Required',
  error: 'Error'
}[status] || 'Unknown');

export default function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.getAdminStatus();
      setStatus(data);
      setAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401) setAuthenticated(false);
      else setMessage('Could not reach the backend. Retrying automatically.');
    }
  }, []);

  useEffect(() => {
    let active = true;
    api.getAdminSession().then(session => {
      if (!active) return;
      setAuthenticated(session.authenticated);
      if (session.authenticated) loadStatus();
    }).catch(() => setMessage('Could not reach the backend.')).finally(() => active && setChecking(false));
    return () => { active = false; };
  }, [loadStatus]);

  useEffect(() => {
    if (!authenticated) return undefined;
    const timer = setInterval(loadStatus, 10000);
    return () => clearInterval(timer);
  }, [authenticated, loadStatus]);

  const login = async event => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await api.adminLogin(password);
      setPassword('');
      setAuthenticated(true);
      await loadStatus();
    } catch (error) {
      setMessage(error.response?.status === 429 ? 'Too many attempts. Please wait and try again.' : 'Incorrect password.');
      setPassword('');
    } finally { setBusy(false); }
  };

  const authorize = async () => {
    setBusy(true);
    try { window.location.assign(await api.getSpotifyAuthorizationUrl()); }
    catch { setMessage('Could not start Spotify authorization.'); setBusy(false); }
  };

  const clearQueue = async () => {
    setBusy(true);
    try { await api.adminClearAll(); setMessage('Jukebox queue cleared.'); await loadStatus(); }
    catch { setMessage('Could not clear the queue.'); }
    finally { setBusy(false); }
  };

  const command = async (type, label) => {
    setBusy(true);
    try { await api.sendPlayerCommand(type); setMessage(`${label} command sent to the kiosk.`); }
    catch { setMessage(`Could not send the ${label.toLowerCase()} command.`); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await api.adminLogout(); } finally { setAuthenticated(false); setStatus(null); }
  };

  if (checking) return <div className="screen admin-screen"><div className="maintenance-card"><h1>Admin</h1><p>Checking session...</p></div></div>;

  if (!authenticated) {
    return (
      <div className="screen admin-screen">
        <div className="admin-login-container">
          <form className="admin-login-content" onSubmit={login}>
            <Settings size={72} />
            <h1>Jukebox Admin</h1>
            <label htmlFor="admin-password">Administrator password</label>
            <input id="admin-password" type="password" autoComplete="current-password" className="admin-password-input"
              value={password} onChange={event => setPassword(event.target.value)} autoFocus />
            <button className="admin-login-button" disabled={busy || !password}>Sign In</button>
            {message && <p className="admin-message error-message" role="alert">{message}</p>}
            <a className="admin-text-link" href="/">Open Jukebox</a>
          </form>
        </div>
      </div>
    );
  }

  const spotify = status?.spotify;
  const player = status?.player;
  return (
    <div className="screen admin-screen">
      <div className="admin-page-header">
        <h1>Jukebox Admin</h1>
        <div className="admin-header-actions">
          <a className="admin-header-button" href="/"><ExternalLink size={22} /> Open Jukebox</a>
          <button className="admin-header-button" onClick={logout}><LogOut size={22} /> Sign Out</button>
        </div>
      </div>
      <div className="admin-content">
        {message && <p className="admin-message" role="status">{message}</p>}
        <section className="admin-status-card">
          <div className="admin-section-title"><h2>Spotify</h2><span className={`status-pill status-${spotify?.status}`}>{statusLabel(spotify?.status)}</span></div>
          <dl className="admin-details-grid">
            <div><dt>Last full authorization</dt><dd>{displayDate(spotify?.fullAuthorizationAt)}</dd></div>
            <div><dt>Estimated reauthorization due</dt><dd>{displayDate(spotify?.estimatedReauthorizationDueAt)}</dd></div>
            <div><dt>Last token refresh</dt><dd>{displayDate(spotify?.lastRefreshAt)}</dd></div>
            <div><dt>Access token expires</dt><dd>{displayDate(spotify?.accessTokenExpiresAt)}</dd></div>
          </dl>
          {spotify?.lastAuthError && <p className="admin-warning">Last authentication error: {spotify.lastAuthError}</p>}
          <div className="admin-inline-actions">
            <button onClick={authorize} disabled={busy}>{spotify?.hasAuthorization ? 'Reauthorize Spotify' : 'Authorize Spotify'}</button>
            <button onClick={loadStatus} disabled={busy}><RefreshCw size={20} /> Refresh Status</button>
          </div>
        </section>

        <section className="admin-status-card">
          <h2>Kiosk and Backend</h2>
          <dl className="admin-details-grid">
            <div><dt>Backend</dt><dd>{status?.backend?.status === 'ok' ? 'Online' : 'Unknown'}</dd></div>
            <div><dt>Kiosk last seen</dt><dd>{displayDate(player?.lastSeenAt)}</dd></div>
            <div><dt>Player</dt><dd>{player?.ready ? 'Ready' : 'Not ready'}</dd></div>
            <div><dt>Device</dt><dd>{player?.deviceId ? `${player.deviceId.slice(0, 8)}...` : 'None'}</dd></div>
            <div><dt>Current track</dt><dd>{player?.currentTrack || 'None reported'}</dd></div>
            <div><dt>Queue</dt><dd>{status?.queue?.total ?? 0} songs</dd></div>
          </dl>
          <div className="admin-action-grid">
            <button className="admin-action-button red" disabled={busy} onClick={clearQueue}><Trash2 size={36} /><span>Clear Queue</span></button>
            <button className="admin-action-button blue" disabled={busy} onClick={() => command('reconnect', 'Reconnect')}><RefreshCw size={36} /><span>Reconnect Player</span></button>
            <button className="admin-action-button green" disabled={busy} onClick={() => command('activate', 'Activate device')}><Volume2 size={36} /><span>Activate Device</span></button>
            <button className="admin-action-button purple" disabled={busy} onClick={() => command('reload', 'Reload')}><RefreshCw size={36} /><span>Reload Kiosk</span></button>
          </div>
        </section>
      </div>
    </div>
  );
}
